<template>
  <nav>
    <GuestHeader v-if="showGuestHeader" :isLoggedIn="isLoggedIn" />
    <div
      v-else
      class="z-10 w-full bg-surface-base px-4 border-linear-bg-subtle border-b flex justify-start items-center"
    >
      <TurntableLogo />
      <VenueSwitchMenu
        :venues="venues"
        class="border-l border-linear-bg-subtle pl-3 self-stretch"
      />
      <div
        id="header-title"
        class="font-heading text-xl font-semibold uppercase mx-auto hidden md:block"
      >
        {{ pageTitle }}
      </div>
      <div class="flex self-stretch text-left mr-4 py-1 ml-auto md:ml-0">
        <OnClickOutside @trigger="closeNav" class="self-center relative z-20">
          <div
            @click="ui.navOpen = !ui.navOpen"
            class="flex items-center justify-center py-6 cursor-pointer"
            id="header-toggle"
          >
            <ActionMenuIcon />
          </div>
          <div
            v-show="ui.navOpen"
            class="absolute right-0 z-10 origin-top-right rounded-md bg-surface-primary shadow-lg -translate-y-2"
            id="header-menu"
          >
            <div class="flex gap-4 p-8">
              <div
                v-if="searchResults.length"
                id="search-results"
                :class="{ 'w-700px mr-12': searchResults.length }"
              >
                <div
                  v-for="result in searchResults"
                  :key="`result-${result.id}`"
                  class="search-result mb-5 p-2 hover:bg-surface-base hover:rounded"
                  @click="closeNav"
                >
                  <SearchResult :result="result" />
                </div>
              </div>
              <div class="flex flex-col gap-4" @click="closeNav">
                <router-link
                  :to="{ name: 'setShow' }"
                  id="venue-show-list"
                  class="header-link text-fg-link-primary block text-base font-normal"
                  >Public Show List</router-link
                >
                <div class="w-full h-0.5 border-b-linear-subtle border-b-2" />
                <div class="flex-col flex gap-2">
                  <PerformanceToolsLink
                    id="venue-performance-tools"
                    class="header-link text-fg-link-primary block text-base font-normal"
                  >
                    Performance Tools
                  </PerformanceToolsLink>
                  <router-link
                    :to="{ name: 'showList' }"
                    id="venue-shows"
                    class="header-link text-fg-link-primary block text-base font-normal"
                    >Shows</router-link
                  >
                  <router-link
                    :to="{ name: 'floorPlans' }"
                    id="venue-floorplans"
                    class="header-link text-fg-link-primary block text-base font-normal"
                    >Floor Plans</router-link
                  >
                  <router-link
                    :to="{ name: 'reservationsDashboard' }"
                    id="venue-dashboard"
                    class="header-link text-fg-link-primary block text-base font-normal"
                    >Dashboard</router-link
                  >
                  <template v-if="isOwner">
                    <router-link
                      :to="{ name: 'reportsOverview' }"
                      id="venue-reports"
                      class="header-link text-fg-link-primary block text-base font-normal"
                      >Reports</router-link
                    >
                  </template>
                </div>
                <SearchInput ref="searchInput" @click.stop />
                <div class="w-full h-0.5 border-b-linear-subtle border-b-2" />
                <VenueThemeSwitch />
                <div class="w-full h-0.5 border-b-linear-subtle border-b-2" />
                <a
                  href="/accounts/logout"
                  id="venue-logout"
                  class="header-link text-fg-link-primary block text-base font-normal"
                  >Logout</a
                >
              </div>
            </div>
          </div>
        </OnClickOutside>
      </div>
      <div v-if="me" id="header-user-icon" class="text-sm">
        {{ me.first_name }}
      </div>
    </div>
  </nav>
</template>
<script lang="ts">
import { OnClickOutside } from '@vueuse/components'
import * as _ from 'lodash-es'
import { mapState, mapStores } from 'pinia'
import { defineComponent } from 'vue'

import ActionMenuIcon from '@/assets/actionMenu.svg?component'
import TurntableLogo from '@/assets/turntableLogo.svg?component'
import SearchInput from '@/components/SearchInput.vue'
import SearchResult from '@/components/SearchResult.vue'
import GuestHeader from '@/components/guest/GuestHeader.vue'
import PerformanceToolsLink from '@/modules/performance/components/atoms/PerformanceToolsLink.vue'
import { PERFORMANCE_ROUTE_NAMES } from '@/modules/performance/constants/PerformanceRouteConstants'
import { usePerformanceStore } from '@/modules/performance/stores/PerformanceStore'
import VenueSwitchMenu from '@/modules/venueUi/components/molecules/VenueSwitchMenu.vue'
import VenueThemeSwitch from '@/modules/venueUi/components/molecules/VenueThemeSwitch.vue'
import { useGuestStore } from '@/stores/guest'
import { useSettingStore } from '@/stores/setting'
import { useUserStore } from '@/stores/user'
import { sortBy } from '@/utils/utils'

import { GuestSearchRead, UserVenueRead } from '@generated/types'

export default defineComponent({
  name: 'HeaderComponent',
  components: {
    TurntableLogo,
    VenueThemeSwitch,
    PerformanceToolsLink,
    VenueSwitchMenu,
    GuestHeader,
    OnClickOutside,
    SearchInput,
    SearchResult,
    ActionMenuIcon,
  },
  data() {
    return {
      ui: {
        navOpen: false,
      },
    }
  },
  async mounted() {
    await this.settingStore.loading
    this.updateTheme()
  },
  computed: {
    PERFORMANCE_ROUTE_NAMES() {
      return PERFORMANCE_ROUTE_NAMES
    },
    ...mapStores(useGuestStore, useSettingStore, usePerformanceStore),
    ...mapState(useUserStore, ['me', 'isLoggedIn', 'isOwner', 'initials']),

    showGuestHeader(): boolean {
      return !!this.$route.meta?.guest
    },
    pageTitle(): string {
      return (this.$route.meta?.pageTitle as string) || (this.$route.name as string) || ''
    },
    searchResults(): GuestSearchRead[] {
      sortBy(this.guestStore.searchResults, 'last_name')
      return this.guestStore.searchResults
    },
    venues(): UserVenueRead[] {
      return _.sortBy(this.me.venues, 'name')
    },
  },
  methods: {
    closeNav(): void {
      this.ui.navOpen ? (this.ui.navOpen = false) : null
    },
    updateTheme(): void {
      if (this.showGuestHeader) {
        document.body.setAttribute('class', 'guest-ui')
      } else {
        document.body.setAttribute('class', 'venue-ui')
      }
    },
  },
  watch: {
    $route() {
      this.ui.navOpen = false
      this.updateTheme()
    },
    'ui.navOpen': function (val) {
      if (val || !this.$refs.searchInput) return
      const searchInput = this.$refs.searchInput as typeof SearchInput
      searchInput.clear()
    },
  },
})
</script>
